import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  inject,
  Injector,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize, map, startWith } from 'rxjs';
import { CampanhaStatus } from '../../core/enum/campanha-status.enum';
import { TipoDestinoCampanha } from '../../core/enum/tipo-destino-campanha.enum';
import { DestinoCampanha } from '../../core/models/destino-campanha.model';
import { Plano } from '../../core/models/plano.model';
import { Perfil } from '../../core/models/perfil.model';
import { CampanhaFormValue } from '../../core/models/campanha-form.model';
import { CampanhaService } from '../../core/services/campanha.service';
import { PerfilService } from '../../core/services/perfil.service';
import { QuestionarioService } from '../../core/services/questionario.service';
import { createCampanhaFormGroup } from '../../core/utils/campanha-form.factory';
import { podeExcluirCampanha } from '../../core/utils/campanha-permissions.util';
import { toLocalIsoDate } from '../../core/validators/campanha-vigencia.validator';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { FormToggleComponent } from '../../shared/components/form-toggle/form-toggle.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ToastService } from '../../core/services/toast.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';

@Component({
  selector: 'app-campanha-form',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    FormToggleComponent,
    BackButtonComponent,
    AlertDialogComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './campanha-form.component.html',
  styleUrl: './campanha-form.component.scss',
})
export class CampanhaFormComponent implements OnInit {
  readonly TipoDestinoCampanha = TipoDestinoCampanha;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly campanhaService = inject(CampanhaService);
  private readonly perfilService = inject(PerfilService);
  private readonly questionarioService = inject(QuestionarioService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);

  readonly campanhaId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id'))),
    { initialValue: null as string | null },
  );

  readonly somenteLeitura = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('modo') === 'visualizar')),
    { initialValue: false },
  );

  readonly isEditMode = computed(() => this.campanhaId() != null);

  readonly pageTitle = computed(() => {
    if (this.somenteLeitura()) {
      return 'Visualizar campanha Portal Participante';
    }

    return this.isEditMode()
      ? 'Editar campanha Portal Participante'
      : 'Criar campanha Portal Participante';
  });

  readonly form: FormGroup = createCampanhaFormGroup(this.fb);

  readonly hoje = toLocalIsoDate(new Date());

  private readonly dtIntegracao = toSignal(
    this.form.controls['dtIntegracao'].valueChanges.pipe(
      startWith(this.form.controls['dtIntegracao'].value as string),
    ),
    { initialValue: this.form.controls['dtIntegracao'].value as string },
  );

  /** Bloco 2.1: `dtIntegracao` vem como ISO da API; exibe `dd/MM/yyyy HH:mm`, ou `-` se vazio. */
  readonly dataHoraIntegracaoLabel = computed(() => {
    const iso = this.dtIntegracao()?.trim();
    if (!iso) {
      return '-';
    }

    const data = formatDateIsoToBr(iso.slice(0, 10));
    const hora = iso.slice(11, 16);
    return hora ? `${data} ${hora}` : data;
  });

  private readonly participanteDataInicio = toSignal(
    this.form.controls['participanteDataInicio'].valueChanges.pipe(
      startWith(this.form.controls['participanteDataInicio'].value as string),
    ),
    { initialValue: this.form.controls['participanteDataInicio'].value as string },
  );

  /**
   * Bloco 2.1: exibe somente a data de `dtIniVigPartic` (`dd/MM/yyyy`), ou `-` se
   * vazia — a hora que a API devolve não é exibida.
   */
  readonly dataInicioPrevistoLabel = computed(() => {
    const data = this.participanteDataInicio()?.trim();
    return data ? formatDateIsoToBr(data) : '-';
  });

  readonly selecionarTodosPlanos = toSignal(
    this.form.controls['selecionarTodosPlanos'].valueChanges.pipe(
      startWith(this.form.controls['selecionarTodosPlanos'].value as boolean),
    ),
    { initialValue: this.form.controls['selecionarTodosPlanos'].value as boolean },
  );

  readonly planosAdicionados = toSignal(
    this.form.controls['planosAdicionados'].valueChanges.pipe(
      startWith(this.form.controls['planosAdicionados'].value as Plano[]),
    ),
    { initialValue: this.form.controls['planosAdicionados'].value as Plano[] },
  );

  readonly planoSearchTerm = signal('');

  readonly showDeleteDialog = signal(false);
  readonly showConfirmarAgendamento = signal(false);
  readonly avisoCampanhaAtivaExistente = signal('');

  private readonly campanhaStatus = signal<CampanhaStatus | undefined>(undefined);

  /** Espelha CampanhaVigenciaGuard.GarantirNaoVigenteEAtiva: só Rascunho ou Agendada aceita exclusão. */
  readonly podeExcluir = computed(() => {
    const status = this.campanhaStatus();
    return this.isEditMode() && status != null && podeExcluirCampanha(status);
  });

  readonly importando = signal(false);
  readonly baseImportada = signal(false);

  private readonly catalogPlanos = signal<Plano[]>([]);
  private readonly catalogDestinos = signal<DestinoCampanha[]>([]);

  readonly tipoDestino = toSignal(
    this.form.controls['tipoDestino'].valueChanges.pipe(
      startWith(this.form.controls['tipoDestino'].value as TipoDestinoCampanha | ''),
    ),
    { initialValue: this.form.controls['tipoDestino'].value as TipoDestinoCampanha | '' },
  );

  readonly destinosFiltrados = computed(() => {
    const tipo = this.tipoDestino();
    if (!tipo) {
      return [];
    }

    return this.catalogDestinos().filter((destino) => destino.tipoDestino === tipo);
  });

  private readonly catalogPerfis = signal<Perfil[]>([]);
  private readonly idsPerfisDoQuestionario = signal<number[]>([]);

  /** Perfis do questionário atual (`destinoId`), para a seção "Perfil Padrão para esta Campanha". */
  readonly perfisDoQuestionario = computed(() => {
    const ids = new Set(this.idsPerfisDoQuestionario());
    return this.catalogPerfis().filter((perfil) => ids.has(perfil.id));
  });

  readonly filteredPlanos = computed(() => {
    const term = this.planoSearchTerm().trim().toLowerCase();
    const addedIds = new Set(this.planosAdicionados().map((plano: Plano) => plano.id));

    return this.catalogPlanos()
      .filter((plano) => !addedIds.has(plano.id))
      .filter((plano) => {
        if (!term) {
          return true;
        }

        return (
          plano.nome.toLowerCase().includes(term) ||
          plano.codigo.toLowerCase().includes(term)
        );
      });
  });

  readonly submitAttempted = signal(false);
  readonly isSaving = signal(false);

  readonly showPlanosRequiredError = computed(
    () =>
      this.form.hasError('planosRequired') &&
      (this.submitAttempted() || this.form.touched),
  );

  showControlError(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (this.submitAttempted() || control.touched);
  }

  showTipoDestinoRequiredError(): boolean {
    return this.showControlError('tipoDestino');
  }

  showDestinoRequiredError(): boolean {
    const destinoControl = this.form.controls['destinoId'];
    return (
      this.form.hasError('destinoRequired') &&
      (this.submitAttempted() || destinoControl.touched || this.form.touched)
    );
  }

  showPerfilPadraoRequiredError(): boolean {
    const perfilPadraoControl = this.form.controls['idPerfilInvestimentoDef'];
    return (
      this.form.hasError('perfilPadraoRequired') &&
      (this.submitAttempted() || perfilPadraoControl.touched || this.form.touched)
    );
  }

  semPerfilPadrao(): boolean {
    return this.form.controls['idPerfilInvestimentoDef'].value == null;
  }

  showDataFimBeforeDataInicioError(): boolean {
    const dataFimControl = this.form.controls['dataFim'];
    return (
      this.form.hasError('dataFimBeforeDataInicio') &&
      (this.submitAttempted() || dataFimControl.touched || this.form.touched)
    );
  }

  // Removido por solicitação do PO — bloco 2.1 virou campo somente leitura, entao o erro
  // de 'participanteDataOutsideVigencia' nao e mais exibido.
  // showParticipanteDataOutsideVigenciaError(): boolean {
  // const participanteControl = this.form.controls['participanteDataInicio'];
  // return (
  // this.form.hasError('participanteDataOutsideVigencia') &&
  // (this.submitAttempted() ||
  // participanteControl.touched ||
  // this.form.touched)
  // );
  // }

  showDataInicioNoPassadoError(): boolean {
    const dataInicioControl = this.form.controls['dataInicio'];
    return (
      this.form.hasError('dataInicioNoPassado') &&
      (this.submitAttempted() || dataInicioControl.touched || this.form.touched)
    );
  }

  showDataFimNoPassadoError(): boolean {
    const dataFimControl = this.form.controls['dataFim'];
    return (
      this.form.hasError('dataFimNoPassado') &&
      (this.submitAttempted() || dataFimControl.touched || this.form.touched)
    );
  }

  // Removido por solicitação do PO — bloco 2.1 virou campo somente leitura, entao o erro
  // de 'participanteDataInicioNoPassado' nao e mais exibido.
  // showParticipanteDataNoPassadoError(): boolean {
  // const participanteControl = this.form.controls['participanteDataInicio'];
  // return (
  // this.form.hasError('participanteDataInicioNoPassado') &&
  // (this.submitAttempted() ||
  // participanteControl.touched ||
  // this.form.touched)
  // );
  // }

  ngOnInit(): void {
    this.form.controls['linkCampanha'].disable({ emitEvent: false });

    // Bloco 2.1: "Data/Hora da Integração" e o horário são sempre somente leitura
    // (valores prontos da API). "Data Início Previsto" acompanha o resto do form —
    // editável em criação e em edição (Rascunho/Agendada); no modo visualizar o
    // `form.disable()` abaixo cobre todos os controls.
    this.form.controls['participanteHorarioInicio'].disable({ emitEvent: false });
    this.form.controls['dtIntegracao'].disable({ emitEvent: false });

    this.campanhaService.listarPlanos().subscribe((planos) => {
      this.catalogPlanos.set(planos);
      // O catálogo chega depois da tela: se a opção já estiver marcada, a lista se completa aqui.
      this.listarTodosOsPlanos();
    });

    this.campanhaService.listarDestinos().subscribe((destinos) => {
      this.catalogDestinos.set(destinos);
    });

    this.perfilService.getPerfis({ status: 'Ativo' }).subscribe((perfis) => {
      this.catalogPerfis.set(perfis);
    });

    this.form.controls['selecionarTodosPlanos'].valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });

    this.form.controls['planosAdicionados'].valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });

    this.syncDestinoControlState(this.form.controls['tipoDestino'].value as TipoDestinoCampanha | '');

    this.form.controls['tipoDestino'].valueChanges.subscribe((tipo) => {
      this.form.controls['destinoId'].setValue('');
      this.form.controls['idPerfilInvestimentoDef'].setValue(null);
      this.syncDestinoControlState(tipo as TipoDestinoCampanha | '');
      this.form.updateValueAndValidity();
    });

    this.syncTermoControlState(this.form.controls['exibeTermo'].value as boolean);

    this.form.controls['exibeTermo'].valueChanges.subscribe((exibeTermo) => {
      this.syncTermoControlState(exibeTermo as boolean);
      this.form.updateValueAndValidity();
    });

    this.form.controls['destinoId'].valueChanges.subscribe((destinoId) => {
      this.form.updateValueAndValidity();
      this.carregarPerfisDoQuestionario(destinoId as string);
    });

    const vigenciaControls = ['dataInicio', 'dataFim', 'participanteDataInicio'] as const;
    for (const controlName of vigenciaControls) {
      this.form.controls[controlName].valueChanges.subscribe(() => {
        this.form.updateValueAndValidity();
      });
    }

    const editId = this.campanhaId();
    if (editId) {
      this.loadCampanhaForEdit(editId);
    } else {
      this.prefillFromClone();
    }
  }

  private prefillFromClone(): void {
    const campanhaClonada = (
      this.router.getCurrentNavigation()?.extras.state ?? history.state
    )?.['campanhaClonada'] as CampanhaFormValue | undefined;

    if (!campanhaClonada) {
      return;
    }

    this.form.patchValue(campanhaClonada);
    this.syncDestinoControlState(campanhaClonada.tipoDestino);
    this.syncTermoControlState(campanhaClonada.exibeTermo);
    this.form.updateValueAndValidity();
  }

  private loadCampanhaForEdit(id: string): void {
    this.campanhaService.obterCampanhaParaEdicao(id).subscribe({
      next: (formValue) => {
        if (!formValue) {
          this.toastService.show('Campanha não encontrada.', 'error');
          void this.router.navigate(['/campanhas']);
          return;
        }

        this.form.patchValue(formValue);
        this.campanhaStatus.set(formValue.status);
        this.syncDestinoControlState(formValue.tipoDestino);
        this.syncTermoControlState(formValue.exibeTermo);
        this.form.updateValueAndValidity();
        this.applyReadonlyMode();
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError
            ? error.message
            : 'Não foi possível carregar a campanha. Tente novamente.';
        this.toastService.show(message, 'error');
        void this.router.navigate(['/campanhas']);
      },
    });
  }

  private syncDestinoControlState(tipo: TipoDestinoCampanha | ''): void {
    const destinoControl = this.form.controls['destinoId'];
    if (tipo) {
      destinoControl.enable({ emitEvent: false });
      return;
    }

    destinoControl.disable({ emitEvent: false });
  }

  private carregarPerfisDoQuestionario(destinoId: string): void {
    const idQuestionario = destinoId ? Number(destinoId) : NaN;
    if (!Number.isFinite(idQuestionario) || idQuestionario <= 0) {
      this.idsPerfisDoQuestionario.set([]);
      this.limparPerfilPadraoForaDoQuestionario();
      return;
    }

    this.questionarioService.listarPontuacoes(idQuestionario).subscribe((pontuacoes) => {
      this.idsPerfisDoQuestionario.set(pontuacoes.map((pontuacao) => pontuacao.idPerfilInvestimento));
      this.limparPerfilPadraoForaDoQuestionario();
    });
  }

  // Ao trocar de questionário o perfil padrão escolhido antes deixa de valer; na edição, o
  // perfil salvo pertence ao questionário carregado e por isso é mantido.
  private limparPerfilPadraoForaDoQuestionario(): void {
    const perfilPadraoControl = this.form.controls['idPerfilInvestimentoDef'];
    const idPerfil = perfilPadraoControl.value as number | null;
    if (idPerfil != null && !this.idsPerfisDoQuestionario().includes(idPerfil)) {
      perfilPadraoControl.setValue(null);
    }
  }

  private syncTermoControlState(exibeTermo: boolean): void {
    const tipoTermoControl = this.form.controls['tipoTermo'];
    if (exibeTermo) {
      tipoTermoControl.enable({ emitEvent: false });
      return;
    }

    tipoTermoControl.disable({ emitEvent: false });
  }

  private applyReadonlyMode(): void {
    if (this.somenteLeitura()) {
      this.form.disable({ emitEvent: false });
    }
  }

  onPlanoSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.planoSearchTerm.set(value);
  }

  addPlano(plano: Plano): void {
    if (this.selecionarTodosPlanos()) {
      return;
    }

    const current = this.form.controls['planosAdicionados'].value as Plano[];
    if (current.some((item) => item.id === plano.id)) {
      return;
    }

    const proximos = [...current, plano];
    this.form.controls['planosAdicionados'].setValue(proximos);
    this.planoSearchTerm.set('');

    // Escolher plano a plano até cobrir o catálogo é a mesma coisa que marcar "todos os
    // planos"; a opção acompanha para a campanha ser gravada como tal.
    const catalogo = this.catalogPlanos();
    if (catalogo.length > 0 && proximos.length >= catalogo.length) {
      this.form.controls['selecionarTodosPlanos'].setValue(true);
    }
  }

  /**
   * Clique na opção "Selecionar todos os planos" — só a interação da pessoa passa por aqui, e
   * não o `patchValue` que carrega a campanha. Marcar lista o catálogo inteiro, que é o que o
   * backend vincula ao salvar; desmarcar esvazia a lista, para a seleção ser montada plano a
   * plano.
   */
  onSelecionarTodosPlanos(event: Event): void {
    const marcado = (event.target as HTMLInputElement).checked;

    this.planoSearchTerm.set('');
    this.form.controls['planosAdicionados'].setValue(marcado ? this.catalogPlanos() : []);
  }

  /**
   * O catálogo chega depois da tela. Se a opção já estiver marcada e a lista vazia, é aqui que
   * ela se completa; numa campanha existente os vínculos vieram da API e ficam como estão.
   */
  private listarTodosOsPlanos(): void {
    const atuais = this.form.controls['planosAdicionados'].value as Plano[];
    if (!this.selecionarTodosPlanos() || atuais.length > 0) {
      return;
    }

    this.form.controls['planosAdicionados'].setValue(this.catalogPlanos());
  }

  removePlano(planoId: string): void {
    const current = this.form.controls['planosAdicionados'].value as Plano[];
    this.form.controls['planosAdicionados'].setValue(
      current.filter((plano) => plano.id !== planoId),
    );

    // Tirar um plano contradiz "todos os planos" — e, com a opção marcada, a gravação manda a
    // lista vazia que o backend expande, devolvendo em silêncio o plano que acabou de sair.
    if (this.selecionarTodosPlanos()) {
      this.form.controls['selecionarTodosPlanos'].setValue(false);
    }
  }

  onArquivoBaseSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    const id = this.campanhaId();

    if (!arquivo || !id) {
      input.value = '';
      return;
    }

    this.importando.set(true);

    this.campanhaService
      .importarParticipantes(Number(id), arquivo)
      .pipe(
        finalize(() => {
          this.importando.set(false);
          input.value = '';
        }),
      )
      .subscribe({
        next: (resultado) => {
          this.toastService.show(
            `${resultado.totalImportados} importados, ${resultado.totalRejeitados} rejeitados de ${resultado.totalProcessados} processados`,
            'success',
          );
          this.baseImportada.set(resultado.totalImportados > 0);
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível importar os participantes.';
          this.toastService.show(message, 'error');
        },
      });
  }

  // Removidos por solicitação do PO — manter comentados (seção 7. Publicação).
  // onCopiarLink(): void {
  //   const link = this.form.controls['linkCampanha'].value as string;
  //
  //   if (!link.trim()) {
  //     this.toastService.show('Não há link da campanha para copiar.', 'info');
  //     return;
  //   }
  //
  //   if (!isPlatformBrowser(this.platformId)) {
  //     return;
  //   }
  //
  //   void this.copyTextToClipboard(link).then(
  //     () => {
  //       this.toastService.show('Link copiado para a área de transferência.', 'success');
  //     },
  //     () => {
  //       this.toastService.show('Não foi possível copiar o link.', 'error');
  //     },
  //   );
  // }
  //
  // private copyTextToClipboard(text: string): Promise<void> {
  //   if (navigator.clipboard?.writeText) {
  //     return navigator.clipboard.writeText(text);
  //   }
  //
  //   return new Promise((resolve, reject) => {
  //     try {
  //       const textarea = document.createElement('textarea');
  //       textarea.value = text;
  //       textarea.setAttribute('readonly', '');
  //       textarea.style.position = 'fixed';
  //       textarea.style.left = '-9999px';
  //       document.body.appendChild(textarea);
  //       textarea.select();
  //       const copied = document.execCommand('copy');
  //       document.body.removeChild(textarea);
  //       copied ? resolve() : reject(new Error('execCommand copy failed'));
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // }

  onSalvarRascunho(): void {
    this.submitAttempted.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.toastService.show(
        'Preencha os campos obrigatórios antes de salvar o rascunho.',
        'error',
      );
      this.scrollToFirstInvalidField();
      return;
    }

    this.isSaving.set(true);
    this.campanhaService.salvarRascunho(this.buildFormPayload()).subscribe({
      next: (campanha) => {
        this.isSaving.set(false);
        this.toastService.show('Rascunho salvo com sucesso.', 'success');
        void this.router.navigate(['/campanhas', campanha.id, 'editar']);
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.toastService.show(this.resolveSaveErrorMessage(error), 'error');
      },
    });
  }

  onAgendar(): void {
    this.submitAttempted.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.toastService.show(
        'Preencha os campos obrigatórios antes de agendar.',
        'error',
      );
      this.scrollToFirstInvalidField();
      return;
    }

    this.campanhaService.listarCampanhas({ status: CampanhaStatus.AtivoPortal }).subscribe({
      next: (campanhasAtivas) => {
        this.avisoCampanhaAtivaExistente.set(
          campanhasAtivas.length > 0
            ? `Já existe uma campanha ativa no portal: "${campanhasAtivas[0].nome}".`
            : '',
        );
        this.showConfirmarAgendamento.set(true);
      },
      error: () => {
        // Falha ao checar campanhas ativas nao deve travar o fluxo de agendamento - segue sem aviso.
        this.avisoCampanhaAtivaExistente.set('');
        this.showConfirmarAgendamento.set(true);
      },
    });
  }

  cancelarAgendamento(): void {
    this.showConfirmarAgendamento.set(false);
  }

  confirmarAgendamento(): void {
    this.showConfirmarAgendamento.set(false);

    this.isSaving.set(true);
    this.campanhaService.agendarCampanha(this.buildFormPayload()).subscribe({
      next: (campanha) => {
        this.isSaving.set(false);
        const link = this.campanhaService.gerarLinkCampanha(campanha);
        this.form.controls['linkCampanha'].setValue(link, { emitEvent: false });
        this.toastService.show('Campanha agendada com sucesso.', 'success');
        void this.router.navigate(['/campanhas', campanha.id]);
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.toastService.show(this.resolveSaveErrorMessage(error), 'error');
      },
    });
  }

  /**
   * markAllAsTouched() só atualiza os controls; as classes is-invalid e os spans de erro
   * só existem no DOM após o próximo render do Angular. afterNextRender garante que a busca
   * abaixo rode depois disso — chamada síncrona aqui encontrava o DOM ainda desatualizado.
   */
  private scrollToFirstInvalidField(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    afterNextRender(
      () => {
        const formElement = document.querySelector('.campanha-form');
        const firstInvalid = formElement?.querySelector(
          '.is-invalid, [data-testid$="-error"]',
        );
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      { injector: this.injector },
    );
  }

  private resolveSaveErrorMessage(error: unknown): string {
    if (error instanceof ApiHttpError) {
      return error.message;
    }

    return 'Não foi possível salvar a campanha. Tente novamente.';
  }

  private buildFormPayload(): CampanhaFormValue {
    const raw = this.form.getRawValue() as CampanhaFormValue;
    const id = this.campanhaId();
    return id ? { ...raw, id } : raw;
  }

  onExcluir(): void {
    if (!this.campanhaId()) {
      return;
    }

    this.showDeleteDialog.set(true);
  }

  cancelarExclusao(): void {
    this.showDeleteDialog.set(false);
  }

  confirmarExclusao(): void {
    const id = this.campanhaId();
    if (!id) {
      return;
    }

    this.showDeleteDialog.set(false);

    this.campanhaService.excluirCampanha(id).subscribe({
      next: (deleted) => {
        if (!deleted) {
          this.toastService.show('Não foi possível excluir a campanha.', 'error');
          return;
        }

        this.toastService.show('Campanha excluída com sucesso.', 'success');
        void this.router.navigate(['/campanhas']);
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError
            ? error.message
            : 'Não foi possível excluir a campanha.';
        this.toastService.show(message, 'error');
      },
    });
  }
}
